/**
 * 리치 텍스트 에디터 컴포넌트 (React Quill 기반)
 */

import React, { useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.css';
import apiClient from '../../services/api';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = '내용을 입력하세요...',
  height = '300px',
}) => {
  const quillRef = useRef<ReactQuill>(null);

  const imageHandler = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // 파일 크기 체크 (10MB 제한)
      if (file.size > 10 * 1024 * 1024) {
        alert('이미지 크기는 10MB 이하여야 합니다.');
        return;
      }

      // 파일 타입 체크
      if (!file.type.startsWith('image/')) {
        alert('이미지 파일만 업로드 가능합니다.');
        return;
      }

      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      const range = quill.getSelection();
      const index = range ? range.index : 0;

      // 업로드 중 표시
      quill.insertText(index, '이미지 업로드 중...', 'user');
      quill.setSelection(index + 1, 0);

      try {
        // 서버에 업로드
        const formData = new FormData();
        formData.append('image', file);

        const response = await apiClient.post<{ data: { url: string } }>(
          '/api/upload/image',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );

        const imageUrl = response.data.data.url;

        // 업로드 중 텍스트 삭제
        quill.deleteText(index, 1);

        // 에디터에 이미지 URL 삽입
        quill.insertEmbed(index, 'image', imageUrl);
        quill.setSelection(index + 1, 0);
      } catch (error: any) {
        console.error('이미지 업로드 실패:', error);
        
        // 업로드 중 텍스트 삭제
        quill.deleteText(index, 1);
        
        const errorMessage = error.response?.data?.message || error.message || '이미지 업로드에 실패했습니다.';
        alert(errorMessage);
      }
    };
  };

  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        'image': imageHandler
      }
    }
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link', 'image', 'video'
  ];

  return (
    <div className="rich-text-editor-wrapper" style={{ height }}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
};

export default RichTextEditor;

